import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, useTheme as useMuiTheme } from '@mui/material';
import { Block as BlockIcon, SearchOff as SearchOffIcon, LockClock as LockClockIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { clearPostLoginRedirect } from '../utils/auth';

export type ChannelErrorVariant = 'accessDenied' | 'channelNotFound' | 'sessionExpired' | 'adminOnly';

export interface AccessDeniedProps {
  /** Optional callback when user clicks back/go button */
  onBack?: () => void;
  /** Optional custom title (overrides variant defaults) */
  title?: string;
  /** Optional custom message (overrides variant defaults) */
  message?: string;
  /** Variant: access denied (403), channel not found (404), or session expired (401) */
  variant?: ChannelErrorVariant;
}

// Default copy, icon, and button label per variant
const VARIANT_CONFIG: Record<
  ChannelErrorVariant,
  { title: string; message: string; buttonLabel: string; Icon: React.ComponentType<{ sx?: object }> }
> = {
  accessDenied: {
    title: 'Access Denied',
    message: `You don't have access to this ${LanguageConstants.topic}.`,
    buttonLabel: `Go to ${LanguageConstants.topic_s}`,
    Icon: BlockIcon,
  },
  channelNotFound: {
    title: `${LanguageConstants.topic} not found`,
    message: `This ${LanguageConstants.topic} does not exist or has been removed.`,
    buttonLabel: `Go to ${LanguageConstants.topic_s}`,
    Icon: SearchOffIcon,
  },
  sessionExpired: {
    title: 'Session Expired',
    message: 'Your session has expired. Please login again.',
    buttonLabel: 'Login',
    Icon: LockClockIcon,
  },
  adminOnly: {
    title: 'Admin Access Only',
    message:
      'This platform is now used by administrators only, for dashboards, data sources, and settings. ' +
      'Please use Gmail, Outlook, or Google Chat to access your support inbox.',
    buttonLabel: 'Back to Login',
    Icon: BlockIcon,
  },
};

/**
 * Full-page view for channel errors: no access (403), channel not found (404), or session expired (401).
 * Uses same layout and consistent button per variant.
 */
const AccessDenied: React.FC<AccessDeniedProps> = ({
  onBack,
  title: titleProp,
  message: messageProp,
  variant = 'accessDenied',
}) => {
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { logout } = useAuth();
  const config = VARIANT_CONFIG[variant];
  const title = titleProp ?? config.title;
  const message = messageProp ?? config.message;
  const buttonLabel = config.buttonLabel;
  const IconComponent = config.Icon;

  // Session expired: logout and go to login; otherwise support space or custom onBack
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (variant === 'sessionExpired' || variant === 'adminOnly') {
      // Clear saved redirect before logout so post-login navigation skips /sessionExpired
      clearPostLoginRedirect();
      logout();
      navigate('/login', { replace: true });
    } else {
      navigate('/supportSpace', { replace: true });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        backgroundColor: isDarkMode
          ? muiTheme.palette.background.default
          : muiTheme.palette.grey[50],
      }}
    >
      {/* Icon (Block for access denied, SearchOff for channel not found) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: muiTheme.palette.error.main + '20',
          mb: 3,
        }}
      >
        <IconComponent
          sx={{
            fontSize: 48,
            color: muiTheme.palette.error.main,
          }}
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          color: muiTheme.palette.text.primary,
          mb: 1,
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>

      {/* Message */}
      <Typography
        variant="body1"
        sx={{
          color: muiTheme.palette.text.secondary,
          textAlign: 'center',
          maxWidth: 400,
          mb: 4,
        }}
      >
        {message}
      </Typography>

      {/* Back / Log in again button */}
      <Button
        variant="contained"
        onClick={handleBack}
        sx={{
          textTransform: 'none',
          px: 3,
          py: 1.5,
        }}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
};

export default AccessDenied;
